import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { orange } from '@/theme/colors'

interface DaysFilterProps {
	dayCount: number
	selectedDays: Set<number>
	mappedCountByDay: Map<number, number>
	onChange: (next: Set<number>) => void
}

const Container = styled.div`
	position: relative;
	padding: 0.75rem 1.25rem;
	flex-shrink: 0;
	border-bottom: 1px solid #f1f3f5;
`

const TriggerButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem 0.9rem;
	border-radius: 8px;
	border: 1.5px solid #dee2e6;
	background: white;
	color: #495057;
	font-weight: 600;
	font-size: 0.85rem;
	cursor: pointer;

	&:hover {
		border-color: #adb5bd;
	}
`

const TriggerLabel = styled.span``

const Caret = styled.span`
	font-size: 0.7rem;
	color: #868e96;
`

const Count = styled.span<{ $active: boolean }>`
	font-size: 0.75rem;
	padding: 0.1rem 0.45rem;
	border-radius: 10px;
	background: ${props => (props.$active ? orange : '#f1f3f5')};
	color: ${props => (props.$active ? 'white' : '#6c757d')};
`

const Popover = styled.div`
	position: absolute;
	top: calc(100% - 0.25rem);
	left: 1.25rem;
	z-index: 50;
	background: white;
	border: 1px solid #e9ecef;
	border-radius: 10px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
	min-width: 240px;
	max-height: 360px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

const PopoverActions = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0.6rem 0.9rem;
	border-bottom: 1px solid #f1f3f5;
`

const ActionLink = styled.button<{ $disabled?: boolean }>`
	background: transparent;
	border: none;
	color: ${props => (props.$disabled ? '#ced4da' : orange)};
	font-weight: 600;
	font-size: 0.8rem;
	cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
	padding: 0;

	&:hover {
		text-decoration: ${props => (props.$disabled ? 'none' : 'underline')};
	}
`

const DayList = styled.div`
	overflow-y: auto;
	padding: 0.35rem 0;
`

const DayRow = styled.label<{ $disabled: boolean }>`
	display: flex;
	align-items: center;
	gap: 0.6rem;
	padding: 0.5rem 0.9rem;
	cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
	opacity: ${props => (props.$disabled ? 0.5 : 1)};
	font-size: 0.9rem;
	color: #212529;

	&:hover {
		background: ${props => (props.$disabled ? 'transparent' : '#f8f9fa')};
	}

	input {
		cursor: inherit;
		accent-color: ${orange};
	}
`

const DayLabel = styled.span`
	flex: 1;
`

const DaysFilter: React.FC<DaysFilterProps> = ({ dayCount, selectedDays, mappedCountByDay, onChange }) => {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (!open) return
		const handleClick = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', handleClick)
		document.addEventListener('keydown', handleKey)
		return () => {
			document.removeEventListener('mousedown', handleClick)
			document.removeEventListener('keydown', handleKey)
		}
	}, [open])

	const allDayIndices = Array.from({ length: dayCount }, (_, i) => i)
	const availableDays = allDayIndices.filter(i => (mappedCountByDay.get(i) ?? 0) > 0)
	const allSelected = availableDays.length > 0 && availableDays.every(i => selectedDays.has(i))

	const toggleDay = (dayIndex: number) => {
		const next = new Set(selectedDays)
		if (next.has(dayIndex)) {
			if (next.size === 1) return
			next.delete(dayIndex)
		} else {
			next.add(dayIndex)
		}
		onChange(next)
	}

	const selectAll = () => onChange(new Set(availableDays))
	const clearAll = () => {
		if (availableDays.length === 0) return
		onChange(new Set([availableDays[0]]))
	}

	const label = (() => {
		if (selectedDays.size === 0) return 'No days'
		if (selectedDays.size === 1) {
			const only = [...selectedDays][0]
			return `Day ${only + 1}`
		}
		if (allSelected) return 'All days'
		return `${selectedDays.size} days`
	})()

	return (
		<Container ref={containerRef}>
			<TriggerButton type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}>
				<TriggerLabel>Filter: {label}</TriggerLabel>
				<Caret>▾</Caret>
			</TriggerButton>
			{open && (
				<Popover role="dialog">
					<PopoverActions>
						<ActionLink type="button" onClick={selectAll} $disabled={allSelected}>
							Select all
						</ActionLink>
						<ActionLink
							type="button"
							onClick={clearAll}
							$disabled={selectedDays.size <= 1}
						>
							Clear
						</ActionLink>
					</PopoverActions>
					<DayList>
						{allDayIndices.map(dayIndex => {
							const count = mappedCountByDay.get(dayIndex) ?? 0
							const disabled = count === 0
							const checked = selectedDays.has(dayIndex)
							return (
								<DayRow key={dayIndex} $disabled={disabled}>
									<input
										type="checkbox"
										checked={checked}
										disabled={disabled}
										onChange={() => toggleDay(dayIndex)}
									/>
									<DayLabel>Day {dayIndex + 1}</DayLabel>
									<Count $active={checked}>{count}</Count>
								</DayRow>
							)
						})}
					</DayList>
				</Popover>
			)}
		</Container>
	)
}

export default DaysFilter
